import { QueryClient } from '@tanstack/react-query';
import { isApiError } from '@sf/api';

/**
 * One QueryClient for the app. Defaults chosen for a monitoring dashboard: data is a few minutes
 * stale at worst, and 4xx never retries (a 404 or a validation error will not fix itself).
 */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 60_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: true,
        retry: (failureCount, error) => {
          if (isApiError(error) && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/** Query keys live here so pages and mutations invalidate the same strings. */
export const qk = {
  tenant: ['tenant'] as const,
  providers: ['providers'] as const,
  me: ['me'] as const,
  devices: ['devices'] as const,
  device: (id: string) => ['devices', id] as const,
  deviceSummary: (id: string) => ['devices', id, 'summary'] as const,
  deviceEnergy: (id: string, granularity: string, from: string, to: string) =>
    ['devices', id, 'energy', granularity, from, to] as const,
  deviceMonthly: (id: string, endYear: number) => ['devices', id, 'energy', 'monthly', endYear] as const,
  deviceIntervals: (id: string, date: string) => ['devices', id, 'intervals', date] as const,
  devicePerformance: (id: string, days: number) => ['devices', id, 'performance', days] as const,
  deviceLookback: (id: string) => ['devices', id, 'lookback'] as const,
  deviceReports: (id: string) => ['devices', id, 'reports'] as const,
  alerts: (status?: string) => ['alerts', status ?? 'all'] as const,
  notifications: (unread?: boolean) => ['notifications', unread ? 'unread' : 'all'] as const,
  /** The inbox page itself, loaded older-and-older into one infinite query. */
  notificationsInbox: ['notifications', 'inbox'] as const,
  subscription: ['subscription'] as const,
  payments: ['subscription', 'payments'] as const,
  withdrawalPreview: ['subscription', 'withdrawal'] as const,
  withdrawals: ['subscription', 'withdrawals'] as const,
  invoices: ['subscription', 'invoices'] as const,
};
